import { assign, createMachine } from "xstate";
import {
  fetchCountries,
  IRoamingInternationalCountry,
} from "@nju/data-access-portal-dashboard";
import { pushSimpleEvent } from "@nju/data-layer";

type IEvent =
  | { type: "SUCCESS"; countries: IRoamingInternationalCountry[] }
  | { type: "SET_COUNTRY_SEARCH"; search: string }
  | { type: "SELECT_COUNTRY"; selectedCountry: IRoamingInternationalCountry }
  | { type: "SUBMIT" }
  | { type: "ERROR" };

interface IContext {
  countries: IRoamingInternationalCountry[];
  filteredCountries: IRoamingInternationalCountry[];
  selectedCountry: IRoamingInternationalCountry | undefined;
  formError: string | undefined;
  countrySearch: string;
}

export const chooseCountryMachine = createMachine(
  {
    context: {
      countries: [],
      filteredCountries: [],
      selectedCountry: undefined,
      formError: undefined,
      countrySearch: "",
    },
    schema: {
      context: {} as IContext,
      events: {} as IEvent,
    },
    initial: "loading",
    states: {
      loading: {
        invoke: {
          src: "fetchCountries",
        },
        on: {
          SUCCESS: {
            actions: "setCountries",
            target: "ready",
          },
          ERROR: {
            target: "error",
          },
        },
      },
      ready: {
        on: {
          SET_COUNTRY_SEARCH: [
            {
              cond: "shouldFilterCountries",
              actions: ["setCountrySearch", "filterCountries"],
            },
            {
              actions: "setCountrySearch",
            },
          ],
          SELECT_COUNTRY: {
            actions: "selectCountry",
          },
          SUBMIT: {
            target: "validate",
          },
        },
      },
      validate: {
        entry: "validateForm",
        always: [
          {
            cond: "hasNoInputErrors",
            target: "success",
          },
          {
            target: "ready",
          },
        ],
      },
      success: {
        entry: "onSubmitSuccess",
      },
      error: {
        type: "final",
      },
    },
  },
  {
    actions: {
      setCountrySearch: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_COUNTRY_SEARCH") {
          return {
            countrySearch: event.search,
          };
        }

        return context;
      }),
      filterCountries: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SET_COUNTRY_SEARCH") {
          return {
            filteredCountries: context.countries.filter(function (country) {
              if (country.searchTags) {
                return country.searchTags
                  .toLowerCase()
                  .includes(event.search.toLowerCase());
              }

              return false;
            }),
          };
        }

        return context;
      }),
      validateForm: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SUBMIT") {
          pushSimpleEvent({
            event: "activity_service",
            eventName: "EnterCountry",
          });

          return {
            formError: context.selectedCountry ? undefined : "Wpisz kraj.",
          };
        }

        return context;
      }),
      setCountries: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SUCCESS") {
          return {
            countries: event.countries,
            filteredCountries: event.countries,
          };
        }

        return context;
      }),
      selectCountry: assign<IContext, IEvent>((context, event) => {
        if (event.type === "SELECT_COUNTRY") {
          return {
            selectedCountry: event.selectedCountry,
            formError: undefined,
          };
        }

        return context;
      }),
    },
    services: {
      fetchCountries: () => {
        return async (callback) => {
          const result = await fetchCountries({
            brand: "NJU",
            category: "INTERNATIONAL",
          });

          if (result.isError) {
            return callback({ type: "ERROR" });
          }

          return callback({ type: "SUCCESS", countries: result.value });
        };
      },
    },
    guards: {
      hasNoInputErrors: ({ formError }) => {
        return formError === undefined;
      },
      shouldFilterCountries: ({ countrySearch }) => {
        return countrySearch.length >= 2;
      },
    },
  },
);
